import { UserOutlined } from '@ant-design/icons';
import { Form, Input, Space, Switch, Typography } from 'antd';
import { useEffect, useRef } from 'react';

import {
  PASSWORD_HINT,
  PERSON_NAME_MAX_LENGTH,
  PHONE_NUMBER_LENGTH,
  passwordRules,
  personNameRules,
  phoneNumberRules,
} from '../../utils/formRules';
import './profilepage.less';

const { Text } = Typography;
const { TextArea } = Input;

/** Ажилтанд олгож болох эрхүүд */
const ROLE_OPTIONS = [
  {
    roleCode: 'merchant-staff',
    roleName: 'Ажилтан',
    roleDescription: 'Захиалга бүртгэх, харах эрхтэй',
  },
  {
    roleCode: 'merchant-admin',
    roleName: 'Админ',
    roleDescription: 'Бүх тохиргоо, ажилтны эрхийг удирдана',
  },
];

const requiredRule = (label) => ({ required: true, message: `${label} дутуу байна!` });

/** `isEdit` — засах горим: нууц үг урьдчилан дүүрэхгүй, хоосон бол хэвээр үлдэнэ */
const UserForm = ({ form, isEdit = false }) => {
  const roleCode = Form.useWatch('roleCode', form);

  /** Формыг ачаалахад эрх нь нэгэнт тавигдсан эсэх — доорх useEffect-д тайлбартай */
  const isFirstRoleSync = useRef(true);

  /**
   * `roleName`-ийг сонгосон `roleCode`-оос дүгнэнэ. Ингэснээр эрх сольсны дараа
   * хуучин нэр үлдэхгүй, мөн серверт `roleCode`, `roleName` хоёр таарч илгээгдэнэ.
   *
   * ГЭХДЭЭ формыг нээх үеийн нэрийг ДАРЖ БИЧИХГҮЙ: сервер тал эрхийн нэрийг
   * «Мерчант Ажилтан» гэж хадгалдаг ч энд «Ажилтан» гэж байдаг тул хэрэглэгч
   * эрхийг хөндөөгүй байхад ч нэр солигдож, зөвхөн овгийг засахад л жагсаалтын
   * «Эрх» багана «Мерчант Ажилтан»-аас «Ажилтан» болж өөрөө өөрчлөгддөг байв.
   */
  useEffect(() => {
    const selected = ROLE_OPTIONS.find((option) => option.roleCode === roleCode);
    if (!selected) return;

    if (isFirstRoleSync.current) {
      isFirstRoleSync.current = false;
      // Засах горимд серверийн нэр аль хэдийн тавигдсан байна — хэвээр үлдээнэ
      if (form.getFieldValue('roleName')) return;
    }

    form.setFieldsValue({ roleName: selected.roleName });
    //eslint-disable-next-line
  }, [roleCode]);

  /** Нэг эрх заавал сонгогдсон байна — унтраавал нөгөө рүү шилжинэ */
  const handleChangeRole = (nextRole, checked) => {
    if (checked) {
      form.setFieldsValue({ roleCode: nextRole });
      return;
    }

    const other = ROLE_OPTIONS.find((option) => option.roleCode !== nextRole);
    form.setFieldsValue({ roleCode: other?.roleCode });
  };

  return (
    <Form form={form} layout="vertical" requiredMark>
      <Form.Item label="Овог" name="lastName" rules={personNameRules('Овог')}>
        <Input size="large" placeholder="Энд бич" maxLength={PERSON_NAME_MAX_LENGTH} />
      </Form.Item>

      <Form.Item label="Нэр" name="firstName" rules={personNameRules('Нэр')}>
        <Input size="large" placeholder="Энд бич" maxLength={PERSON_NAME_MAX_LENGTH} />
      </Form.Item>

      <Form.Item label="Утас" name="phoneNumber" rules={phoneNumberRules('Утас дутуу байна!')}>
        <Input size="large" placeholder="Энд бич" maxLength={PHONE_NUMBER_LENGTH} />
      </Form.Item>

      <Form.Item
        label="Нэвтрэх мэйл"
        name="email"
        rules={[requiredRule('Нэвтрэх мэйл'), { type: 'email', message: 'И-Мэйл буруу байна.' }]}
      >
        <Input size="large" placeholder="Энд бич" />
      </Form.Item>

      <Form.Item
        label="Нууц үг"
        name="password"
        rules={passwordRules(!isEdit)}
        extra={isEdit ? `Солихгүй бол хоосон үлдээнэ. ${PASSWORD_HINT}` : PASSWORD_HINT}
      >
        <Input.Password
          size="large"
          placeholder={isEdit ? 'Шинэ нууц үг' : 'Энд бич'}
          autoComplete="new-password"
        />
      </Form.Item>

      <Form.Item label="Ажилтны тайлбар" name="description">
        <TextArea rows={3} maxLength={200} showCount placeholder="Энд бич" />
      </Form.Item>

      {/* Сонгосон эрх — доорх жагсаалтаас удирдана. Бүртгэлтэй байснаар payload-д орно */}
      <Form.Item name="roleCode" hidden>
        <Input />
      </Form.Item>

      <Form.Item name="roleName" hidden>
        <Input />
      </Form.Item>

      <Form.Item label="Эрх олгох" style={{ marginBottom: 0 }}>
        <div className="user_role_list">
          {ROLE_OPTIONS.map((option) => (
            <div key={option.roleCode} className="user_role_option">
              <Space size={16} align="center">
                <UserOutlined style={{ fontSize: 16, color: '#8993A5' }} />
                <Space direction="vertical" size={0}>
                  <Text>{option.roleName}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {option.roleDescription}
                  </Text>
                </Space>
              </Space>
              <Switch
                checked={option.roleCode === roleCode}
                onChange={(checked) => handleChangeRole(option.roleCode, checked)}
              />
            </div>
          ))}
        </div>
      </Form.Item>
    </Form>
  );
};

export default UserForm;
